import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function usePushNotifications(emailId) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);

  // Check support and fetch VAPID key
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setIsSupported(true);

    axios.get(`${API}/push/vapid-key`)
      .then((res) => setVapidKey(res.data.public_key))
      .catch(() => {}); // Push not configured on server
  }, []);

  // Check current subscription status
  useEffect(() => {
    if (!isSupported || !emailId) return;

    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(!!sub);
      });
    });
  }, [isSupported, emailId]);

  const subscribe = useCallback(async () => {
    if (!vapidKey || !emailId) return false;

    try {
      const reg = await navigator.serviceWorker.ready;

      // Convert VAPID key to Uint8Array
      const padding = "=".repeat((4 - (vapidKey.length % 4)) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, "+").replace(/_/g, "/");
      const rawData = atob(base64);
      const applicationServerKey = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; i++) {
        applicationServerKey[i] = rawData.charCodeAt(i);
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        email_id: emailId,
        subscription: subscription.toJSON(),
      });

      setIsSubscribed(true);
      return true;
    } catch (e) {
      console.error("Push subscribe error:", e);
      return false;
    }
  }, [vapidKey, emailId]);

  const unsubscribe = useCallback(async () => {
    if (!emailId) return;

    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();

      if (subscription) {
        await axios.post(`${API}/push/unsubscribe`, {
          email_id: emailId,
          subscription: subscription.toJSON(),
        });
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (e) {
      console.error("Push unsubscribe error:", e);
    }
  }, [emailId]);

  return { isSupported: isSupported && !!vapidKey, isSubscribed, subscribe, unsubscribe };
}
